import React, { useState, useEffect, useCallback, useMemo, useRef, memo } from 'react';
import { Marker, Tooltip, Popup } from 'react-leaflet';
import L from 'leaflet';
import PropTypes from 'prop-types';
import { 
  Box, Text, Badge, VStack, HStack, Icon, Button, Flex
} from '@chakra-ui/react';
import { 
  MdSensors, MdWifi, MdBatteryFull, MdLocationOn, 
  MdRouter, MdVisibility, MdWarning, MdCheckCircle, MdAccessTime
} from 'react-icons/md';
import { FaCogs } from 'react-icons/fa';
import './SensorMarkers.css';

// ============================================================================
// DEBUG UTILITY
// ============================================================================

class SensorMarkersDebugger {
  constructor() {
    this.logs = [];
    this.maxLogs = 500;
    this.performanceMarks = new Map();
    this.renderCount = 0;
    this.statusUpdateCount = 0;
    this.clickCount = 0;
  }

  log(level, action, data = {}) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      level,
      action,
      component: 'SensorMarkers',
      ...data,
      renderCount: this.renderCount
    };

    this.logs.push(logEntry);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    const colors = {
      INFO: '#2196F3',
      SUCCESS: '#4CAF50',
      WARNING: '#FF9800',
      ERROR: '#F44336',
      DEBUG: '#9C27B0',
      PERFORMANCE: '#00BCD4'
    };

    console.log(
      `%c[${level}] ${action}`,
      `color: ${colors[level]}; font-weight: bold;`,
      data
    );

    try {
      const storedLogs = JSON.parse(sessionStorage.getItem('sensor_markers_logs') || '[]');
      storedLogs.push(logEntry);
      if (storedLogs.length > this.maxLogs) storedLogs.shift();
      sessionStorage.setItem('sensor_markers_logs', JSON.stringify(storedLogs));
    } catch (e) {
      console.warn('Failed to store logs:', e);
    }
  }

  startPerformance(marker) {
    this.performanceMarks.set(marker, performance.now());
  }

  endPerformance(marker, action) {
    const start = this.performanceMarks.get(marker);
    if (start) {
      const duration = performance.now() - start;
      this.log('PERFORMANCE', action, {
        duration: `${duration.toFixed(2)}ms`,
        marker
      });
      this.performanceMarks.delete(marker);
      return duration;
    }
    return null;
  }

  incrementRender() {
    this.renderCount++;
  }

  incrementStatusUpdate() {
    this.statusUpdateCount++;
  }

  incrementClick() {
    this.clickCount++;
  }

  getStats() {
    return {
      totalLogs: this.logs.length,
      renderCount: this.renderCount,
      statusUpdateCount: this.statusUpdateCount,
      clickCount: this.clickCount,
      recentLogs: this.logs.slice(-10)
    };
  }

  exportLogs() {
    const dataStr = JSON.stringify(this.logs, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `sensor-markers-debug-${Date.now()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }

  clearLogs() {
    this.logs = [];
    sessionStorage.removeItem('sensor_markers_logs');
    this.log('INFO', 'logs_cleared');
  }
}

const markersDebugger = new SensorMarkersDebugger();

if (typeof window !== 'undefined') {
  window.__SensorMarkersDebug__ = {
    getLogs: () => markersDebugger.logs,
    getStats: () => markersDebugger.getStats(),
    exportLogs: () => markersDebugger.exportLogs(),
    clearLogs: () => markersDebugger.clearLogs()
  };
}

// ============================================================================
// SENSOR STATUS UTILITIES
// ============================================================================

const SensorStatus = {
  ONLINE: 'online',
  OFFLINE: 'offline',
  WARNING: 'warning',
  ERROR: 'error',
  MAINTENANCE: 'maintenance'
};

const getStatusColor = (status) => {
  const colors = {
    [SensorStatus.ONLINE]: '#4CAF50',
    [SensorStatus.OFFLINE]: '#9E9E9E',
    [SensorStatus.WARNING]: '#FF9800',
    [SensorStatus.ERROR]: '#F44336',
    [SensorStatus.MAINTENANCE]: '#2196F3'
  };
  return colors[status?.toLowerCase()] || '#9E9E9E';
};

const getStatusIcon = (sensorType) => {
  const icons = {
    'Temperature': 'thermometer-half',
    'Humidity': 'tint',
    'Pressure': 'tachometer-alt',
    'Motion': 'walking',
    'Light': 'lightbulb',
    'Sound': 'volume-up',
    'Vibration': 'wave-square',
    'Flow': 'water',
    'water_level': 'water',
    'default': 'broadcast-tower'
  };
  return icons[sensorType] || icons.default;
};

// ============================================================================
// SINGLE SENSOR MARKER COMPONENT
// ============================================================================

const popupAnimation = `
  @keyframes slideUpFade {
    0% { opacity: 0; transform: translateY(10px) scale(0.95); }
    100% { opacity: 1; transform: translateY(0) scale(1); }
  }
`;

const SensorMarker = memo(({ 
  sensor, 
  isSelected, 
  onClick,
  showTooltip = true,
  animatePulse = true,
  readings = {},
  sensorPipelineLinks = {}
}) => {
  markersDebugger.log('DEBUG', 'sensor_marker_render', {
    sensorId: sensor.id,
    status: sensor.status,
    isSelected
  });

  const markerRef = useRef(null); 

  const icon = useMemo(() => {
    markersDebugger.startPerformance(`create_icon_${sensor.id}`);
    
    const statusColor = getStatusColor(sensor.status);
    const iconName = getStatusIcon(sensor.type);
    const pulseClass = animatePulse && (sensor.status === SensorStatus.ONLINE || sensor.status === 'active') ? 'pulse' : '';
    const selectedClass = isSelected ? 'selected' : '';
    const lowBattery = sensor.batteryLevel && sensor.batteryLevel < 25 ? 'low-battery' : '';
    
    const iconHtml = `
      <div class="sensor-marker ${pulseClass} ${selectedClass} ${lowBattery}" 
            style="background-color: ${statusColor}">
        <i class="fas fa-${iconName}"></i>
        ${sensor.batteryLevel && sensor.batteryLevel < 25 ? 
          '<span class="battery-warning">!</span>' : ''}
      </div>
    `;

    const customIcon = L.divIcon({
      className: 'custom-sensor-icon',
      html: iconHtml,
      iconSize: [32, 32],
      iconAnchor: [16, 16],
      popupAnchor: [0, -16]
    });

    markersDebugger.endPerformance(`create_icon_${sensor.id}`, 'icon_created');
    return customIcon;
  }, [sensor.id, sensor.status, sensor.type, sensor.batteryLevel, isSelected, animatePulse]);

  const handleClick = useCallback(() => {
    markersDebugger.incrementClick();
    markersDebugger.log('INFO', 'sensor_clicked', {
      sensorId: sensor.id,
      sensorName: sensor.name,
      status: sensor.status,
      type: sensor.type
    });
    
    if (onClick) {
      onClick(sensor);
    }
  }, [sensor, onClick]);

  useEffect(() => {
    if (isSelected && markerRef.current) {
      const marker = markerRef.current;
      const element = marker._icon;
      
      if (element) {
        element.classList.add('bounce-animation');
        setTimeout(() => {
          element.classList.remove('bounce-animation');
        }, 1000);
      }
    }
  }, [isSelected]);

  if (sensor._parsedLat === undefined || sensor._parsedLng === undefined) {
    return null;
  }

  // --- Dynamic Data Extraction for UI ---
  const lookupId = sensor.device_id || sensor.id;
  const displayId = sensor.name || lookupId;
  
  // Cross-reference live data dictionaries passed from parent
  const liveReading = readings[lookupId];
  
  // --- UPDATE PHASE 4: Polymorphic Link Extraction ---
  const isLinkedToAsset = sensor.linked_entity_type && sensor.linked_entity_type !== 'none' && sensor.linked_entity_type !== 'pipeline';
  const isLinkedToPipeline = sensor.linked_entity_type === 'pipeline' || !!sensorPipelineLinks[lookupId] || !!sensor.pipeline_id;
  
  let linkLabel = 'Infrastructure';
  let linkValueText = 'NOT LINKED';
  let badgeColor = 'gray';

  if (isLinkedToAsset) {
    linkLabel = 'Asset';
    linkValueText = `${(sensor.linked_entity_type || 'Asset').toUpperCase()} #${sensor.linked_entity_id}`;
    badgeColor = 'teal'; // Use a distinct color for physical assets
  } else if (isLinkedToPipeline) {
    linkLabel = 'Pipeline';
    const pId = sensor.linked_entity_id || sensorPipelineLinks[lookupId] || sensor.pipeline_id;
    linkValueText = `Pipeline #${pId}`;
    badgeColor = 'purple';
  }
  // --- END UPDATE ---
  
  // Determine Live Status
  const isOnline = !!liveReading || sensor.status === 'online' || sensor.status === 'active';
  const displayStatus = liveReading ? 'ONLINE' : (sensor.status || 'OFFLINE').toUpperCase();
  
  // Formulate Values robustly
  let currentReadingValue = 'N/A';
  if (liveReading) {
    currentReadingValue = liveReading.psi ?? liveReading.pressure ?? liveReading.level ?? 'N/A';
  } else {
    currentReadingValue = sensor.psi ?? sensor.lastValue ?? 'N/A';
  }

  // Format cleanly to 1 decimal place if it's a valid number
  if (typeof currentReadingValue === 'number') {
    currentReadingValue = currentReadingValue.toFixed(1);
  }
  
  const unit = sensor.type === 'water_level' ? 'ft' : (sensor.unit || 'PSI');
  const updateTime = liveReading?.timestamp ? new Date(liveReading.timestamp).toLocaleTimeString() : 'N/A';

  return (
    <Marker
      ref={markerRef}
      position={[sensor._parsedLat, sensor._parsedLng]}
      icon={icon}
      eventHandlers={{
        click: handleClick,
        mouseover: () => {
          markersDebugger.log('DEBUG', 'sensor_hover', { sensorId: sensor.id });
        }
      }}
    >
      {/* Tooltip */}
      {showTooltip && (
        <Tooltip direction="top" offset={[0, -10]} opacity={0.9}>
          <div className="sensor-tooltip">
            <strong>{displayId}</strong>
            <div className="sensor-status" style={{ color: getStatusColor(isOnline ? 'online' : sensor.status) }}>
              <i className={`fas fa-circle`}></i> {displayStatus}
            </div>
          </div>
        </Tooltip>
      )}

      {/* UPDATE: Increased minWidth to 290 to prevent horizontal cutoff of the active badge */}
      <Popup minWidth={290} maxWidth={350} className="sensor-custom-popup" closeButton={false}>
        <style>{popupAnimation}</style>
        
        {/* Modern Chakra UI Popup Container */}
        <Box p={2} w="290px" bg="white" borderRadius="lg">
          
          {/* Header */}
          <HStack justify="space-between" mb={3} borderBottomWidth="1px" pb={3} borderColor="gray.200">
            <HStack spacing={3}>
              <Flex p={2} bg="blue.500" borderRadius="md" color="white" shadow="sm">
                <Icon as={MdSensors} boxSize={4} />
              </Flex>
              {/* Max width increased so the ID isn't heavily truncated */}
              <Text fontWeight="800" fontSize="sm" color="gray.800" noOfLines={1} maxW="135px" title={displayId}>
                {displayId}
              </Text>
            </HStack>
            <Badge 
              colorScheme={isOnline ? 'green' : 'red'} 
              variant="subtle" 
              px={2} py={0.5} 
              borderRadius="full"
              display="flex" alignItems="center" gap={1}
            >
              <Icon as={isOnline ? MdCheckCircle : MdWarning} />
              {displayStatus}
            </Badge>
          </HStack>

          {/* Key Metric: PSI / Telemetry */}
          <Box bg="blue.50" p={3} borderRadius="lg" mb={4} textAlign="center" borderWidth="1px" borderColor="blue.100">
            <Text fontSize="xs" color="blue.600" fontWeight="700" textTransform="uppercase" mb={1}>Live Telemetry</Text>
            <HStack justify="center" align="baseline" spacing={1}>
              <Text fontSize="2xl" fontWeight="900" color="blue.600" lineHeight="1">{currentReadingValue}</Text>
              <Text fontSize="sm" fontWeight="700" color="blue.400">{currentReadingValue !== 'N/A' ? unit : ''}</Text>
            </HStack>
            <HStack justify="center" mt={2} color="gray.500" spacing={1}>
                <Icon as={MdAccessTime} boxSize={3} />
                <Text fontSize="10px" fontWeight="600">Updated: {updateTime}</Text>
            </HStack>
          </Box>

          {/* Details Grid */}
          <VStack align="stretch" spacing={2} mb={4}>
            
            <HStack justify="space-between">
              <HStack color="gray.500"><Icon as={FaCogs} boxSize={3}/><Text fontSize="xs" fontWeight="600">Type</Text></HStack>
              <Text fontSize="xs" fontWeight="800" color="gray.700" textTransform="capitalize">{sensor.type || 'Standard'}</Text>
            </HStack>
            
            {/* --- UPDATE PHASE 4: Dynamic Link Display in Popup --- */}
            <HStack justify="space-between">
              <HStack color="gray.500"><Icon as={MdRouter} boxSize={3}/><Text fontSize="xs" fontWeight="600">{linkLabel}</Text></HStack>
              {(isLinkedToAsset || isLinkedToPipeline) ? (
                <Badge colorScheme={badgeColor} fontSize="10px">{linkValueText}</Badge>
              ) : (
                <Text fontSize="xs" fontWeight="800" color="gray.400">NOT LINKED</Text>
              )}
            </HStack>
            {/* --- END UPDATE --- */}

            <HStack justify="space-between">
              <HStack color="gray.500"><Icon as={MdBatteryFull} boxSize={3}/><Text fontSize="xs" fontWeight="600">Battery</Text></HStack>
              <Text fontSize="xs" fontWeight="800" color={sensor.batteryLevel < 25 ? 'red.500' : 'green.500'}>
                {sensor.batteryLevel !== undefined ? `${sensor.batteryLevel}%` : '100%'}
              </Text>
            </HStack>

            <HStack justify="space-between">
              <HStack color="gray.500"><Icon as={MdLocationOn} boxSize={3}/><Text fontSize="xs" fontWeight="600">Coords</Text></HStack>
              <Text fontSize="xs" fontWeight="800" color="gray.700" fontFamily="mono">
                {sensor._parsedLat.toFixed(4)}, {sensor._parsedLng.toFixed(4)}
              </Text>
            </HStack>

          </VStack>

          {/* Action Button */}
          <Button 
            size="sm" 
            colorScheme="blue" 
            w="full" 
            leftIcon={<MdVisibility />}
            borderRadius="md"
            onClick={(e) => {
              e.stopPropagation();
              if (onClick) onClick(sensor);
            }}
            _hover={{ transform: 'translateY(-2px)', shadow: 'md' }}
            transition="all 0.2s"
          >
            View Full Details
          </Button>
        </Box>
      </Popup>
    </Marker>
  );
}, (prevProps, nextProps) => {
  // Safe lookup ID generation for memoized updates
  const prevId = prevProps.sensor.device_id || prevProps.sensor.id;
  const nextId = nextProps.sensor.device_id || nextProps.sensor.id;

  return (
    prevProps.sensor.id === nextProps.sensor.id &&
    prevProps.sensor.status === nextProps.sensor.status &&
    prevProps.sensor.batteryLevel === nextProps.sensor.batteryLevel &&
    prevProps.sensor.lastReading === nextProps.sensor.lastReading &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.showTooltip === nextProps.showTooltip &&
    prevProps.animatePulse === nextProps.animatePulse &&
    prevProps.readings?.[prevId] === nextProps.readings?.[nextId] &&
    prevProps.sensorPipelineLinks?.[prevId] === nextProps.sensorPipelineLinks?.[nextId]
  );
});

SensorMarker.displayName = 'SensorMarker';

// ============================================================================
// MAIN SENSOR MARKERS COMPONENT
// ============================================================================

const SensorMarkers = ({
  sensors = [],
  selectedSensor = null,
  onSensorClick,
  showTooltips = true,
  animatePulse = true,
  filterByStatus = null,
  filterByType = null,
  readings = {},
  sensorPipelineLinks = {}
}) => {
  markersDebugger.incrementRender();
  markersDebugger.log('INFO', 'component_render', {
    sensorCount: sensors.length,
    hasSelectedSensor: !!selectedSensor,
    filterByStatus,
    filterByType
  });

  // ============================================================================
  // STATE
  // ============================================================================

  const [sensorStatuses, setSensorStatuses] = useState({});
  const statusPollingRef = useRef(null);

  // ============================================================================
  // MEMOIZED VALUES
  // ============================================================================

  // Filter sensors AND AGGRESSIVELY PARSE COORDINATES
  const filteredSensors = useMemo(() => {
    markersDebugger.startPerformance('filter_sensors');
    
    let filtered = sensors.filter(s => {
      // Handle both naming conventions: latitude/longitude OR lat/lng
      const rawLat = s.latitude !== undefined ? s.latitude : s.lat;
      const rawLng = s.longitude !== undefined ? s.longitude : s.lng;
      
      const lat = parseFloat(rawLat);
      const lng = parseFloat(rawLng);

      if (isNaN(lat) || isNaN(lng)) {
        markersDebugger.log('WARNING', 'dropped_invalid_sensor_coords', { 
          id: s.device_id || s.id, 
          rawLat, 
          rawLng 
        });
        return false;
      }
      
      // Attach the successfully parsed floats to the object to guarantee rendering
      s._parsedLat = lat;
      s._parsedLng = lng;
      return true;
    });
    
    if (filterByStatus) {
      filtered = filtered.filter(s => s.status === filterByStatus);
    }
    
    if (filterByType) {
      filtered = filtered.filter(s => s.type === filterByType);
    }
    
    markersDebugger.endPerformance('filter_sensors', 'sensors_filtered');
    markersDebugger.log('DEBUG', 'sensors_filtered', {
      total: sensors.length,
      filtered: filtered.length,
      filterByStatus,
      filterByType
    });
    
    return filtered;
  }, [sensors, filterByStatus, filterByType]);

  // Group sensors by status
  const sensorsByStatus = useMemo(() => {
    markersDebugger.startPerformance('group_sensors_by_status');
    
    const grouped = filteredSensors.reduce((acc, sensor) => {
      const status = sensor.status || SensorStatus.OFFLINE;
      if (!acc[status]) {
        acc[status] = [];
      }
      acc[status].push(sensor);
      return acc;
    }, {});
    
    markersDebugger.endPerformance('group_sensors_by_status', 'sensors_grouped');
    markersDebugger.log('DEBUG', 'sensors_grouped_by_status', {
      online: grouped[SensorStatus.ONLINE]?.length || 0,
      offline: grouped[SensorStatus.OFFLINE]?.length || 0,
      warning: grouped[SensorStatus.WARNING]?.length || 0,
      error: grouped[SensorStatus.ERROR]?.length || 0
    });
    
    return grouped;
  }, [filteredSensors]);

  // Sensors with low battery
  const lowBatterySensors = useMemo(() => {
    return filteredSensors.filter(s => 
      s.batteryLevel !== undefined && s.batteryLevel < 25
    );
  }, [filteredSensors]);

  // ============================================================================
  // CALLBACKS
  // ============================================================================

  const handleSensorClick = useCallback((sensor) => {
    markersDebugger.log('INFO', 'sensor_click_handler', {
      sensorId: sensor.id,
      sensorName: sensor.name
    });
    
    if (onSensorClick) {
      onSensorClick(sensor);
    }
  }, [onSensorClick]);

  // Update sensor statuses (simulated real-time updates)
  const updateSensorStatuses = useCallback(async () => {
    markersDebugger.log('DEBUG', 'updating_sensor_statuses');
    markersDebugger.incrementStatusUpdate();
    
    try {
      // In real implementation, fetch from API
      // For now, simulate status changes
      const updates = {};
      
      filteredSensors.forEach(sensor => {
        // Simulate occasional status changes
        if (Math.random() < 0.05) { // 5% chance of status change
          const statuses = Object.values(SensorStatus);
          const newStatus = statuses[Math.floor(Math.random() * statuses.length)];
          updates[sensor.id] = newStatus;
        }
      });
      
      if (Object.keys(updates).length > 0) {
        setSensorStatuses(prev => ({ ...prev, ...updates }));
        markersDebugger.log('SUCCESS', 'sensor_statuses_updated', {
          updatedCount: Object.keys(updates).length
        });
      }
    } catch (error) {
      markersDebugger.log('ERROR', 'status_update_failed', {
        error: error.message
      });
    }
  }, [filteredSensors]);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Status polling
  useEffect(() => {
    markersDebugger.log('INFO', 'starting_status_polling', { interval: '15s' });
    
    // Initial update
    updateSensorStatuses();
    
    // Poll every 15 seconds
    statusPollingRef.current = setInterval(() => {
      updateSensorStatuses();
    }, 15000);
    
    return () => {
      if (statusPollingRef.current) {
        clearInterval(statusPollingRef.current);
        markersDebugger.log('INFO', 'status_polling_stopped');
      }
    };
  }, [updateSensorStatuses]);

  // Log prop changes
  useEffect(() => {
    markersDebugger.log('DEBUG', 'props_updated', {
      sensorCount: sensors.length,
      selectedSensor: selectedSensor?.id,
      filterByStatus,
      filterByType
    });
  }, [sensors, selectedSensor, filterByStatus, filterByType]);

  // Warn about low battery sensors
  useEffect(() => {
    if (lowBatterySensors.length > 0) {
      markersDebugger.log('WARNING', 'low_battery_sensors_detected', {
        count: lowBatterySensors.length,
        sensors: lowBatterySensors.map(s => ({
          id: s.id,
          name: s.name,
          battery: s.batteryLevel
        }))
      });
    }
  }, [lowBatterySensors]);

  // ============================================================================
  // RENDER
  // ============================================================================

  if (filteredSensors.length === 0) {
    markersDebugger.log('WARNING', 'no_sensors_to_display');
    return null;
  }

  markersDebugger.log('DEBUG', 'rendering_markers', {
    count: filteredSensors.length
  });

  return (
    <>
      {filteredSensors.map(sensor => {
        // Apply real-time status updates
        const updatedSensor = {
          ...sensor,
          status: sensorStatuses[sensor.id] || sensor.status || 'online' // Default to online if unknown
        };
        
        return (
          <SensorMarker
            key={sensor.id || sensor.device_id}
            sensor={updatedSensor}
            isSelected={selectedSensor && (selectedSensor.id === sensor.id || selectedSensor.device_id === sensor.device_id)}
            onClick={handleSensorClick}
            showTooltip={showTooltips}
            animatePulse={animatePulse}
            readings={readings}
            sensorPipelineLinks={sensorPipelineLinks}
          />
        );
      })}
    </>
  );
};

// ============================================================================
// PROP TYPES
// ============================================================================

SensorMarkers.propTypes = {
  sensors: PropTypes.array,
  selectedSensor: PropTypes.object,
  onSensorClick: PropTypes.func,
  showTooltips: PropTypes.bool,
  animatePulse: PropTypes.bool,
  filterByStatus: PropTypes.oneOf(Object.values(SensorStatus)),
  filterByType: PropTypes.string,
  readings: PropTypes.object,
  sensorPipelineLinks: PropTypes.object
};

// Performance optimization with React.memo
export default memo(SensorMarkers, (prevProps, nextProps) => {
  const isEqual =
    prevProps.sensors === nextProps.sensors &&
    prevProps.selectedSensor?.id === nextProps.selectedSensor?.id &&
    prevProps.showTooltips === nextProps.showTooltips &&
    prevProps.animatePulse === nextProps.animatePulse &&
    prevProps.filterByStatus === nextProps.filterByStatus &&
    prevProps.filterByType === nextProps.filterByType &&
    prevProps.readings === nextProps.readings &&
    prevProps.sensorPipelineLinks === nextProps.sensorPipelineLinks;

  if (!isEqual) {
    markersDebugger.log('DEBUG', 'component_rerender_triggered', {
      sensorsChanged: prevProps.sensors !== nextProps.sensors,
      selectedSensorChanged: prevProps.selectedSensor?.id !== nextProps.selectedSensor?.id,
      filtersChanged: prevProps.filterByStatus !== nextProps.filterByStatus || 
                      prevProps.filterByType !== nextProps.filterByType
    });
  }

  return isEqual;
});